import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui';
import { Icon } from './Icon';

/* ------------------------------------------------------------------ *
 * Toast
 * ------------------------------------------------------------------ */

type ToastTone = 'ok' | 'danger' | 'info';

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastApi {
  push: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const push = useCallback((message: string, tone: ToastTone = 'info') => {
    const id = nextId.current++;
    setItems((current) => [...current, { id, message, tone }]);
    // 3.5 秒足够读完一句中文提示，又不至于挡住后续操作。
    window.setTimeout(() => setItems((current) => current.filter((item) => item.id !== id)), 3500);
  }, []);

  const api = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        通知挂在 body 上而不是当前容器里：任何带 backdrop-filter / transform 的祖先
        都会成为 fixed 后代的包含块，那样提示会被困在一个小盒子里。
      */}
      {createPortal(
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 p-4 sm:items-end"
          role="status"
          aria-live="polite"
        >
          {items.map((item) => (
            <div
              key={item.id}
              className={`pointer-events-auto flex max-w-[min(24rem,calc(100vw-2rem))] items-start gap-2 rounded-control border px-3 py-2 text-[13px] shadow-sm ${
                item.tone === 'ok'
                  ? 'border-ok/30 bg-panel text-ok'
                  : item.tone === 'danger'
                    ? 'border-danger/30 bg-panel text-danger'
                    : 'border-line bg-panel text-fg'
              }`}
            >
              <Icon
                name={item.tone === 'ok' ? 'check' : item.tone === 'danger' ? 'alert' : 'help'}
                className="mt-0.5 h-4 w-4 shrink-0"
              />
              <span className="text-fg">{item.message}</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  // 没有 Provider 时降级成 console：宁可提示丢了，也不要整页崩掉。
  return context ?? { push: (message: string) => console.info(`[toast] ${message}`) };
}

/* ------------------------------------------------------------------ *
 * Modal
 * ------------------------------------------------------------------ */

export interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * 模态框。
 *
 * 必须用 portal 挂到 body：模态框的祖先里只要有一个带 `backdrop-filter`
 * （顶栏就是），`position: fixed` 的包含块就会变成那个元素，遮罩只会盖住顶栏那一条。
 */
export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  size = 'md',
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    // 打开时锁滚动，关闭时恢复原值（而不是硬写成空串）。
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // 聚焦到面板本身，避免焦点留在触发按钮上导致 Tab 走出模态框。
    const timer = window.setTimeout(() => panelRef.current?.focus(), 0);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(timer);
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' } as const;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/40 p-0 sm:items-center sm:p-6">
      <button
        type="button"
        aria-label="关闭"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
        tabIndex={-1}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={`relative w-full ${widths[size]} max-h-[90vh] overflow-y-auto rounded-t-card bg-panel p-5 sm:rounded-card`}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-fg">{title}</h2>
            {description ? <p className="mt-1 text-[13px] text-fg-muted">{description}</p> : null}
          </div>
          <Button size="sm" variant="ghost" onClick={onClose} title="关闭">
            <Icon name="close" className="h-4 w-4" />
          </Button>
        </div>

        {children}

        {footer ? <div className="mt-5 flex justify-end gap-2">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

/** 危险动作的二次确认，把「不可逆」这件事写清楚。 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = '确认',
  onConfirm,
  onCancel,
  busy = false,
}: {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  return (
    <Modal
      open={open}
      title={title}
      description={description}
      onClose={onCancel}
      size="sm"
      footer={
        <>
          <Button onClick={onCancel} disabled={busy}>
            取消
          </Button>
          <Button variant="danger" onClick={onConfirm} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}
